/**
 * Role Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import {
    RoleFormSchema,
    AssignRoleUsersToRoleSchema,
    SetRoleUnitsSchema,
    SetRoleUnitAssignmentsSchema
} from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class RoleService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'Role listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch role list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch role');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        // Rule: Validation at boundary
        const validation = RoleFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Role oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create role');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        // Validate before update
        const validation = RoleFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Role güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update role');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Role silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete role');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            // Use optimized batch delete endpoint
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            // Fallback to sequential delete if batch endpoint fails
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Get roles for dropdown selection
     * @returns {Promise<Object>}
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('RoleService');
        try {
            const response = await withRetry(
                () => this.api.client.get(`${this.api.basePath}/dropdown/params`),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data?.data || [],
                message: 'Dropdown verisi getirildi'
            };
        } catch (error) {
            // Fallback: use getAll without pagination to populate dropdown
            try {
                const response = await this.getAll({ pageSize: 1000 });
                const dropdownData = (response.data || []).map(r => ({
                    value: r._id || r.id,
                    label: r.name || r.roleName
                }));
                return {
                    success: true,
                    data: dropdownData,
                    message: 'Dropdown verisi getirildi (fallback)'
                };
            } catch (fallbackError) {
                throw this._handleError(fallbackError, 'Failed to fetch dropdown data');
            }
        }
    }

    /**
     * Custom operation: createClone
     */
    async createClone(id, data = {}) {
        try {
            const response = await this.api.client.post(`/${id}/clone`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createClone');
        }
    }

    /**
     * Custom operation: createSetDefault
     */
    async createSetDefault(id, data = {}) {
        try {
            const response = await this.api.client.post(`/${id}/set-default`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createSetDefault');
        }
    }

    /**
     * Add tenant users to a role (merges into membership.roles).
     */
    async assignUsersToRole(roleId, userIds) {
        const validation = AssignRoleUsersToRoleSchema.safeParse({ userIds });
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz kullanıcı listesi',
                validation.error.format()
            );
        }
        try {
            const body = await this.api.assignUsers(roleId, validation.data.userIds);
            return {
                success: true,
                updated: body.updated ?? 0,
                skippedNoMembership: body.skippedNoMembership || [],
                alreadyHad: body.alreadyHad || [],
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to assign users to role');
        }
    }

    /**
     * Replace the units (units) linked to this role.
     * @param {string} roleId
     * @param {string[]} unitIds
     */
    async setUnits(roleId, unitIds) {
        const validation = SetRoleUnitsSchema.safeParse({ unitIds });
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz birim listesi',
                validation.error.format()
            );
        }
        try {
            const body = await this.api.setUnits(roleId, validation.data.unitIds);
            return {
                success: true,
                data: body.data,
                message: body.message || 'Birimler güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update role units');
        }
    }

    /**
     * Replace (or merge) the per-unit user assignments for this role.
     * Returns conflict info (HTTP 409) when forceReplace is not set.
     * @param {string} roleId
     * @param {Array<{ unit: string, user: string|null }>} assignments
     * @param {{ forceReplace?: boolean, merge?: boolean }} [options]
     */
    async setUnitAssignments(roleId, assignments, options = {}) {
        const validation = SetRoleUnitAssignmentsSchema.safeParse({
            assignments,
            forceReplace: Boolean(options.forceReplace),
            merge: Boolean(options.merge),
        });
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz birim-rol atamaları',
                validation.error.format()
            );
        }
        try {
            const body = await this.api.setUnitAssignments(
                roleId,
                validation.data.assignments,
                {
                    forceReplace: validation.data.forceReplace,
                    merge: validation.data.merge,
                }
            );
            return {
                success: true,
                data: body.data,
                message: body.message || 'Birim-rol atamaları güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update unit-role assignments');
        }
    }

    /**
     * List all unit-role assignments associated with a unit.
     * @param {string} unitId
     */
    async listUnitRolesForUnit(unitId) {
        try {
            const body = await this.api.listUnitRolesForUnit(unitId);
            return {
                success: true,
                data: body.data || [],
                message: body.message || 'Birim rolleri getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to list unit-roles for unit');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
